import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { Microservices } from '../../../core/constants/pol.enums';
import {
  safeSessionStorageGetItem,
  safeSessionStorageRemoveItem,
  safeSessionStorageSetItem,
} from '../../../utils/safe-session-storage';
import {
  IAssessmentsRequest,
  TEraPreAssessmentPayload,
  IPreAssessmentPayload,
  IAssessmentUserProfileRequest,
  IAssessmentUserProfileResponse,
  EAssessmentType,
} from '../assessments.model';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';
import { assertNotEmpty } from 'app/utils/assert';

@Injectable({ providedIn: 'root' })
export class AssessmentsService {
  private readonly assessmentTokenKey = 'era-assessment-token';
  private readonly http = inject(HttpClient);
  private readonly oidc = inject(OidcClientService);

  private assessmentsRequest: IAssessmentsRequest | null = null;

  public createUserProfile(
    assessmentId: number,
    assessmentType: EAssessmentType,
    securitySettingId: string,
    memberId?: string
  ): Observable<IAssessmentsRequest> {
    const apiUrl = `${Microservices.Assessments}/UserProfile`;

    const loggedInUsername = this.oidc.getUser().profile.preferred_username;
    assertNotEmpty(loggedInUsername);

    const payload: IAssessmentUserProfileRequest = {
      applicationInfo: {
        assessmentNpmVersion: '22.1.3',
        name: 'polng',
      },
      assessmentId,
      externalId: loggedInUsername,
      polNg: {
        memberId, //optional only needed for specific assessments with member info
        securitySettingId, // selected security setting id
        userId: loggedInUsername, // loggedIn user id
      },
      sourceKey: 'polng',
      type: assessmentType,
    };

    return this.http.post<IAssessmentUserProfileResponse | string>(apiUrl, payload).pipe(
      map((response: IAssessmentUserProfileResponse | string) => {
        const token = this.resolveToken(response);
        const consumerPersonaId =
          typeof response === 'object' && response !== null && typeof response.consumerPersonaId === 'number'
            ? response.consumerPersonaId
            : undefined;

        return {
          assessmentsToken: token,
          consumerPersonaId,
        };
      })
    );
  }

  /**
   * A helper to create the payload for preassessments in the proper structure
   * input:
   * { foo: 'bar', bar: 'baz' }
   *
   * output:
   * {
   *   data: [
   *     { key: 'foo', value: 'bar' },
   *     { key: 'bar', value: 'baz' }
   *   ]
   * }
   */
  private createPreAssessmentPayload(data: Record<string, string>): IPreAssessmentPayload {
    const payload: IPreAssessmentPayload = { data: [] };

    for (const key in data) {
      if (Object.hasOwn(data, key)) {
        const value = data[key];
        payload.data.push({ key, value });
      }
    }

    return payload;
  }

  public createEraPreAssessment(data: TEraPreAssessmentPayload): Observable<{ securitySettingId: string }> {
    const payload = this.createPreAssessmentPayload(data);
    const url = `${Microservices.ProviderOnline}/PreAssessments`;

    return this.http.post<{ securitySettingId: string }>(url, payload);
  }

  public setAssessmentsRequest(request: IAssessmentsRequest): void {
    this.assessmentsRequest = request;
    safeSessionStorageRemoveItem(this.assessmentTokenKey);

    if (request.assessmentsToken !== null) {
      safeSessionStorageSetItem(this.assessmentTokenKey, request.assessmentsToken);
    }
  }

  public getAssessmentsRequest(): IAssessmentsRequest | null {
    if (this.assessmentsRequest !== null) {
      return this.assessmentsRequest;
    }

    const assessmentsToken = safeSessionStorageGetItem(this.assessmentTokenKey);

    return assessmentsToken === null ? null : { assessmentsToken };
  }

  private resolveToken(response: IAssessmentUserProfileResponse | string): string | null {
    if (typeof response === 'string') {
      return response;
    }
    return response.assessmentsToken ?? response.token ?? null;
  }
}
